import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ServiceZone {
  id: string;
  country_code: string;
  country_name: string;
  country_slug: string;
  region_name: string | null;
  city_name: string;
  slug: string;
  description: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  image_url: string | null;
  flag_emoji: string | null;
  transport_info: Record<string, unknown>;
  services_available: string[];
  distances: Record<string, unknown>;
  work_opportunities: string[];
  amenities: string[];
  coordinates: Record<string, unknown>;
  office_info: Record<string, unknown>;
  is_active: boolean;
  is_featured: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CountryGroup {
  country_code: string;
  country_name: string;
  country_slug: string;
  flag_emoji: string;
  zones: ServiceZone[];
  count: number;
}

// Fetch all active zones
export function useZones() {
  return useQuery({
    queryKey: ["service-zones"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_zones")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as ServiceZone[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export interface CountrySummary {
  country_code: string;
  country_name: string;
  country_slug: string;
  count: number;
  center: [number, number] | null;
}

// Fetch zones grouped by country (slim payload + computed center)
export function useZonesGroupedByCountry() {
  return useQuery({
    queryKey: ["service-zones-grouped-slim"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_zones")
        .select("country_code,country_name,country_slug,coordinates")
        .eq("is_active", true)
        .range(0, 19999);

      if (error) throw error;

      const grouped: Record<string, CountrySummary & { _latSum: number; _lngSum: number; _n: number }> = {};
      (data || []).forEach((z: any) => {
        const key = z.country_slug;
        if (!grouped[key]) {
          grouped[key] = {
            country_code: z.country_code,
            country_name: z.country_name,
            country_slug: z.country_slug,
            count: 0,
            center: null,
            _latSum: 0,
            _lngSum: 0,
            _n: 0,
          };
        }
        grouped[key].count++;
        const c = z.coordinates as { lat?: unknown; lng?: unknown } | null;
        if (c) {
          const lat = typeof c.lat === "number" ? c.lat : parseFloat(String(c.lat));
          const lng = typeof c.lng === "number" ? c.lng : parseFloat(String(c.lng));
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            grouped[key]._latSum += lat;
            grouped[key]._lngSum += lng;
            grouped[key]._n++;
          }
        }
      });

      return Object.values(grouped).map((g) => ({
        country_code: g.country_code,
        country_name: g.country_name,
        country_slug: g.country_slug,
        count: g.count,
        center: g._n > 0 ? ([g._latSum / g._n, g._lngSum / g._n] as [number, number]) : null,
      }));
    },
    staleTime: 10 * 60 * 1000,
  });
}

// Fetch zones for a specific country
export function useZonesByCountry(countrySlug: string) {
  return useQuery({
    queryKey: ["service-zones", "country", countrySlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_zones")
        .select("*")
        .eq("country_slug", countrySlug)
        .eq("is_active", true)
        .order("display_order", { ascending: true })
        .range(0, 1999);

      if (error) throw error;
      return data as ServiceZone[];
    },
    enabled: !!countrySlug,
    staleTime: 10 * 60 * 1000,
  });
}

// Fetch a single zone by country and city slug
export function useZone(countrySlug: string, citySlug: string) {
  return useQuery({
    queryKey: ["service-zones", countrySlug, citySlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_zones")
        .select("*")
        .eq("country_slug", countrySlug)
        .eq("slug", citySlug)
        .eq("is_active", true)
        .single();

      if (error) throw error;
      return data as ServiceZone;
    },
    enabled: !!countrySlug && !!citySlug,
    staleTime: 10 * 60 * 1000,
  });
}

// Fetch featured zones
export function useFeaturedZones() {
  return useQuery({
    queryKey: ["service-zones", "featured"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_zones")
        .select("*")
        .eq("is_active", true)
        .eq("is_featured", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as ServiceZone[];
    },
    staleTime: 10 * 60 * 1000,
  });
}

// Search zones
export function useSearchZones(searchTerm: string) {
  return useQuery({
    queryKey: ["service-zones", "search", searchTerm],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_zones")
        .select("*")
        .eq("is_active", true)
        .or(`city_name.ilike.%${searchTerm}%,country_name.ilike.%${searchTerm}%,region_name.ilike.%${searchTerm}%`)
        .order("display_order", { ascending: true })
        .limit(10);

      if (error) throw error;
      return data as ServiceZone[];
    },
    enabled: searchTerm.length >= 2,
    staleTime: 5 * 60 * 1000,
  });
}
